import React, { FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

import DataTable from 'components/ui/DataTable/data-table';
import FormikValidationError from 'components/ui/FormikErrors';
import Modal from 'components/ui/Modal/modal';
import PrimaryButton from 'components/ui/PrimaryButton';
import SecondaryButton from 'components/ui/SecondaryButton';
import useToggle from 'hooks/useToggle';

import { useListTraineeQuery } from 'redux/reducers/auth';
import {
  useEnrollTraineeInRoutineMutation,
  useListRoutineEnrollmentQuery
} from 'redux/reducers/routine-enrollment';
import { inviteTraineeInitialValues, InviteTraineeSchemaValidations } from './schema';

interface IInviteUsersModal {
  selectedRoutine: string;
}

const InviteUsersModal: FC<IInviteUsersModal> = ({ selectedRoutine }) => {
  const [inviteTraineeModal, toggleInviteTraineeModal] = useToggle(false);
  const [page, setPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [selectedObjects, setSelectedObjects] = useState<any[]>([]);

  // API Queries
  const { data: routineEnrollUserLists } = useListRoutineEnrollmentQuery({
    routineId: selectedRoutine
  });

  // Trigger API call when debouncedSearch changes
  const { data: searchResults, isFetching: isSearching } = useListTraineeQuery({ searchQuery: debouncedSearch, pageNo: 1, pageSize: 10 }, {
    skip: !inviteTraineeModal // Only fetch if modal is open
  });

  const [enrollTrainee, { isLoading: isAddingTrainee }] = useEnrollTraineeInRoutineMutation();

  // Debounce logic
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const formik = useFormik<any>({
    enableReinitialize: true,
    validationSchema: InviteTraineeSchemaValidations,
    initialValues: inviteTraineeInitialValues,
    onSubmit: (values) => {
      onEnroll(values.trainee);
    }
  });
  console.log(formik.errors);

  useEffect(() => {
    if (routineEnrollUserLists?.data) {
      const ids = routineEnrollUserLists.data.map((u: any) => u.id.toString());
      formik.setFieldValue('trainee', ids);
      setSelectedObjects(routineEnrollUserLists.data);
    }
  }, [routineEnrollUserLists]);

  const onEnroll = async (traineeIds: string[]) => {
    try {
      await enrollTrainee({
        routine: +selectedRoutine,
        trainee: traineeIds.map((t) => +t)
      }).unwrap();
      toggleInviteTraineeModal();
      toast.success('Trainees updated successfully!');
    } catch (error) {
      toast.error('Failed to update trainees!');
    }
  };

  const handleAdd = (user: any) => {
    const id: any = user.id.toString();
    if (!formik.values.trainee.includes(id)) {
      formik.setFieldValue('trainee', [...formik.values.trainee, id]);
      setSelectedObjects((prev) => [...prev, user]);
    }
  };

  const handleRemove = (id: string) => {
    formik.setFieldValue('trainee', formik.values.trainee.filter((item: string) => item !== id));
    setSelectedObjects((prev) => prev.filter((obj) => obj.id.toString() !== id));
  };

  const totalUser = routineEnrollUserLists?.data || [];
  const paginatedData = totalUser.slice((page - 1) * 10, page * 10);

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <Modal
          title="Manage Routine Enrollment"
          isModalOpen={inviteTraineeModal}
          closeModal={toggleInviteTraineeModal}
          modalSize="modal-md"
          renderFooter={() => (
            <div className="hstack gap-2 justify-content-end">
              <SecondaryButton onClick={toggleInviteTraineeModal} title="Close" />
              <PrimaryButton title="Save Changes" type="submit" isLoading={isAddingTrainee} />
            </div>
          )}
        >
          {/* 1. Selected List (Horizontal Badges) */}
          <div className="mb-4">
            <label className="form-label fw-bold">Selected Trainees ({selectedObjects.length})</label>
            <div className="d-flex flex-wrap gap-2 p-2 border rounded bg-light" style={{ minHeight: '60px', maxHeight: '120px', overflowY: 'auto' }}>
              {selectedObjects.length === 0 && <span className="text-muted small">No trainees selected.</span>}
              {selectedObjects.map((user) => (
                <div key={user.id} className="badge bg-primary d-flex align-items-center gap-2 p-2" style={{ fontSize: '0.85rem' }}>
                  <span>{user.first_name} {user.last_name}</span>
                  <i
                    className="ri-close-fill cursor-pointer text-white"
                    onClick={() => handleRemove(user.id.toString())}
                  ></i>
                </div>
              ))}
            </div>
          </div>

          {/* 2. Search Input */}
          <div className="mb-3">
            <label className="form-label fw-bold">Search New Trainees</label>
            <div className="position-relative">
              <input
                type="text"
                className="form-control"
                placeholder="Start typing name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {isSearching && (
                <div className="spinner-border spinner-border-sm position-absolute end-0 top-50 translate-middle-y me-3" role="status"></div>
              )}
            </div>
          </div>

          {/* 3. Search Results List */}
          <div className="list-group border rounded overflow-auto" style={{ maxHeight: '250px' }}>
            {searchResults?.data?.length > 0 ? (
              searchResults.data.map((user: any) => {
                const isAlreadySelected: any = formik.values.trainee.includes(user?.id);
                return (
                  <div key={user.id} className="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-0 text-sm">{user.first_name} {user.last_name}</h6>
                      <small className="text-muted">{user.mobile_number} | {user.user?.[0]?.email}</small>
                    </div>
                    <button
                      type="button"
                      disabled={isAlreadySelected}
                      className={`btn btn-sm ${isAlreadySelected ? 'btn-light' : 'btn-outline-primary'}`}
                      onClick={() => handleAdd(user)}
                    >
                      {isAlreadySelected ? 'Added' : 'Add'}
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="p-4 text-center text-muted">
                {searchTerm.length > 0 ? "No trainees found." : "Type to search trainees..."}
              </div>
            )}
          </div>

          <FormikValidationError name="trainee" errors={formik.errors} touched={formik.touched} />
        </Modal>
      </form>

      {/* Main Table Display */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6>Current Enrollment - {totalUser.length}</h6>
        <PrimaryButton title="Manage Enrollment" type="button" onClick={toggleInviteTraineeModal} />
      </div>

      <DataTable
        search={false}
        columns={[
          { Header: 'S.N', accessor: 'sn', Cell: (data: any) => <>{data.row.index + (page - 1) * 10 + 1}</> },
          { Header: 'First Name', accessor: 'first_name' },
          { Header: 'Last Name', accessor: 'last_name' },
          { Header: 'Phone', accessor: 'mobile_number' }
        ]}
        data={paginatedData}
        tableHeaders={() => <></>}
        pagination={{
          canPrevious: page > 1,
          canNext: page * 10 < totalUser.length,
          prevPage: () => setPage((p) => Math.max(p - 1, 1)),
          nextPage: () => setPage((p) => p + 1)
        }}
      />
    </div>
  );
};

export default InviteUsersModal;